import {
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Chip,
  Stack,
  SxProps,
  Theme,
  Typography,
} from '@mui/material'
import { Icons } from 'utoniq-ui'

import {
  ArtistTopEventFragment,
  Event,
  EventListFragment,
  EventPreSaleConnectionFragment,
  GlobalEventFragment,
  Maybe,
} from '@/clients/utoniq-core/schema'
import { styleSets } from '@/components/styles'
import { config } from '@/config'
import { useRouter, useTranslation } from '@/core'
import { Organism } from '@/core/architecture'
import { useAnalytics } from '@/lib/analytics'

import { useEventCategoryTag } from './useEventCategoryTag'

export type EventCardProps = {
  event: EventListFragment | GlobalEventFragment | ArtistTopEventFragment
  sx?: SxProps<Theme>
}

export const EventCard: Organism<EventCardProps> = ({ event, sx }) => {
  const { generatePath, routerPages } = useRouter()
  const { analytics } = useAnalytics()

  return (
    <Card sx={{ borderRadius: 2, ...sx }}>
      <CardActionArea
        href={generatePath(routerPages.ShokufesEventDetail, {
          eventId: event.id,
        })}
        sx={{ height: '100%' }}
        onClick={() => {
          analytics?.track('click_select', {
            applicationKey: config.applicationKey,
            eventId: event.id,
            name: 'eventFes',
          })
        }}
      >
        <CardMedia
          sx={{ height: 160 }}
          image={event.thumbnail?.mediumUri ?? ''}
          title={event.title}
        />
        <CardContent sx={{ p: 4, '&:last-child': { pb: 4 } }}>
          {!!(event.startedAt || event.endedAt) && (
            <EventCategory event={event} />
          )}
          <Typography variant="h6" sx={{ pt: 2, ...styleSets.lineCamp(2) }}>
            {event.title}
          </Typography>
          {event.dateRange && (
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ pt: 2 }}
            >
              <Icons.Calendar fontSize="small" sx={{ color: 'grey.700' }} />
              <Typography variant="overline" color="text.secondary">
                {event.dateRange}
              </Typography>
            </Stack>
          )}
          {event.location && (
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ pt: 1 }}
            >
              <Icons.Pin fontSize="small" sx={{ color: 'grey.700' }} />
              <Typography variant="overline" color="text.secondary">
                {event.location?.approximateAddress}
              </Typography>
            </Stack>
          )}
        </CardContent>
      </CardActionArea>
    </Card>
  )
}

type EventCategoryProps = {
  event: Pick<Event, 'startedAt' | 'endedAt'> & {
    preSales: Maybe<EventPreSaleConnectionFragment>
  }
}
export const EventCategory: Organism<EventCategoryProps> = ({ event }) => {
  const { t } = useTranslation('events')

  const { color, bgcolor, text } = useEventCategoryTag(event, t)

  return (
    <Chip
      size="small"
      sx={{
        height: 20,
        borderRadius: '2px',
        minWidth: 40,
        bgcolor,
        '.MuiChip-label': {
          px: 1,
        },
      }}
      label={
        <Typography color={color} variant="overline" display="block">
          {text}
        </Typography>
      }
    />
  )
}
